"use client";

/**
 * ## Banner3 — side effects
 * 
 * Details of the all the side effects (useEffect/useLayoutEffect) in the Banner3 component.
 * It is difficult to pinpoint the exact order of side effects, but the following table is general gist of side
 * effects.
 *
 * **Markers** (prefixed on each hook):
 * - `[LAYOUT]` — `useLayoutEffect`: runs before browser paint; use for measuring DOM and avoiding flicker.
 * - `[FX]` — `useEffect`: runs after paint; use for subscriptions, timers, and non-layout browser APIs.
 *
 * **Index**
 * | ID  | Hook                         | Role                                                                                         |
 * |-----|------------------------------|----------------------------------------------------------------------------------------------|
 * | S1  | `[LAYOUT][hydrate]`          | Initial `innerWidth` / `innerHeight`, `prefers-reduced-motion`, `min-width:1080px`.          |
 * | S2  | `[FX][mq]`                   | Re-subscribe when `matchMedia` fires (motion + breakpoint).                                   |
 * | S3  | `[LAYOUT][measure]`          | `ResizeObserver` on hero content + sticky shell → `heroMeasure`.                             |
 * | S4  | `[FX][media-gate]`           | When to allow hero video / image carousel (preview, slides-only, or `IntersectionObserver`). |
 * | S5  | `[FX][carousel-reset]`       | New slide set → reset active index.                                                          |
 * | S6  | `[FX][carousel-tick]`        | Interval to advance background slides (no video, multi-slide, motion OK).                    |
 * | S7  | `[FX][video-reset-mute]`     | New video URL → unmute intent back to default.                                               |
 * | S8  | `[FX][video-play]`           | Autoplay + fallback to muted if browser blocks unmuted play.                                 |
 * | S9  | `[FX][viewport]`             | `resize` → viewport state, wheel parallax target, section offset for wheel lock.             |
 * | S10 | `[FX][parallax-wheel]`       | Desktop wheel hijack: parallax 0→1, then buffer until unlock scroll.                         |
 * | S11 | `[FX][parallax-scroll-lock]` | Until parallax unlock, clamp `window.scrollY` to banner top.                                 |
 * | S12 | `[FX][analytics]`            | Fire widget-viewed once when section is live (non-preview).                                  |
 */

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

/**
 * Single source of truth for Banner3 layout (Figma Atlas / 4731 / 3583 mobile).
 * Breakpoints match `--breakpoint-sm` … `--breakpoint-xl` (1080 / 1230 / 1430 / 1750).
 */
const BANNER3 = (() => {
  const bp = { sm: 1080, md: 1230, lg: 1430, xl: 1750 } as const;
  const finalNudgeUp = 44;
  const subtitleMdUp = 508;
  const title2Lg = 680;
  const subtitleXl = Math.round((subtitleMdUp * bp.xl) / bp.lg);
  const title2Xl = Math.round((title2Lg * bp.xl) / bp.lg);

  return {
    bp,
    wheel: {
      deltaViewportMultiplier: 1.25,
      scrollUnlockProgress: 1.25,
    },
    text: {
      leftOffsetStartPx: 360,
      ctaFinalLeftPx: 54,
      finalStateNudgeUpPx: finalNudgeUp,
      topFigmaEndPx: 182 - finalNudgeUp,
      bottomPadPx: 32,
      safeTopPx: 48,
      safeBottomPx: 16,
    },
    hero: {
      /** Must match `stickyHeroRef` Tailwind heights */
      stickyHeightPx: { sm: 771, md: 805, lg: 980, xl: 1080 },
      estimatedStackFallback: 460,
      bgCrossfadeMs: 700,
    },
    desktop: {
      /** Bottom-align stack height estimate by band (title_1 + title_2 + subtitle + gaps). */
      estimatedStackPx: { xl: 390, lg: 380, md: 330, sm: 290 },
      initialNudgeDownPx: { xl: 100, lg: 125, md: 120, sm: 120, default: 28 },
      subtitleLerpStartPx: 712,
      subtitleMaxEndPx: { sm: 432, mdUp: subtitleMdUp, xl: subtitleXl },
      title2: {
        endMaxPx: { md: 620, lg: title2Lg, xl: title2Xl },
        initialMaxSmPx: 560,
        lerpStartMdUpPx: 720,
        endWidthBelowMdPx: 468,
      },
      cta: {
        topPx: 468 - finalNudgeUp,
        endTopNudgeFromLgPx: 30,
        endMarginTopPx: 20,
        estHeightPx: 56,
        gapAboveTextPx: 30,
        parallaxStartLeftPx: -278,
      },
      ctaExtraBelowCopyPx: { lg: 96, md: 83, sm: 67 },
    },
    mobile: {
      frameWidthPx: 375,
      textTopFigmaPx: 524,
      textTopNudgeUpPx: 72,
      /** Figma stack top minus `textTopNudgeUpPx` and hero-height trim; extra `+32` nudges title block slightly down. */
      textTopPx: 524 - 72 + 20 - 50,
      /** −100px vs Figma CTA top so CTA stays inside shortened hero. */
      ctaTopPx: 723 - 100,
      ctaWidthPx: 206,
      ctaHeightPx: 44,
      textPadXPx: 12,
      /** `frameWidthPx - 2 * textPadXPx` (375 frame minus gutters). */
      headlineInnerMaxWPx: 375 - 24,
    },
    accred: {
      parallaxBottomStartPx: 60,
      parallaxBottomEndPx: { default: 120, mdBand: 50 },
      stripWidthPx: { xl: 1470, lg: 1430, md: 1230, sm: 1079 },
    },
    copy: {
      maxTitle1Len: 20,
      maxTitle2Len: 20,
      maxSubtitleLen: 130,
      maxHeroBgSlides: 12,
    },
    video: {
      end: {
        sm: {
          widthPct: (515 / 1080) * 100,
          endHeightPx: 313,
          leftPct: ((525 + 515 / 2) / 1080) * 100,
        },
        md: {
          widthPct: (630 / 1230) * 100,
          endHeightPx: 383,
          leftPct: ((560 + 630 / 2) / 1230) * 100,
        },
        lg: {
          widthPct: (740 / 1430) * 100,
          endHeightPx: 450,
          leftPct: ((650 + 740 / 2) / 1430) * 100,
        },
        lgFrame: {
          widthPct: (740 / 1430) * 100,
          endHeightPx: 450,
          leftPct: ((650 + 740 / 2) / 1430) * 100,
          endWidthPx: 740,
        },
        xl: {
          widthPct: (924 / 1750) * 100,
          endHeightPx: 450,
          leftPct: ((706 + 924 / 2) / 1750) * 100,
          endWidthPx: 924,
        },
      },
    },
  } as const;
})();

/** Parallax end-state video box (Figma sm / md / lg / xl). */
type VideoEndLayout = {
  widthPct: number;
  endHeightPx: number;
  leftPct: number;
  endWidthPx?: number;
};

/** Viewport-based layout helpers (all read from `BANNER3`). */
const heroLayout = {
  accredBottomEnd(vp: number): number {
    const { bp, accred } = BANNER3;
    if (vp >= bp.md && vp < bp.lg) return accred.parallaxBottomEndPx.mdBand;
    return accred.parallaxBottomEndPx.default;
  },

  desktopStickyHeight(vp: number): number | null {
    const { bp, hero } = BANNER3;
    const h = hero.stickyHeightPx;
    if (vp >= bp.xl) return h.xl;
    if (vp >= bp.lg) return h.lg;
    if (vp >= bp.md) return h.md;
    if (vp >= bp.sm) return h.sm;
    return null;
  },

  estimatedTextBlockHeight(vp: number): number {
    const { bp, hero, desktop } = BANNER3;
    const e = desktop.estimatedStackPx;
    if (vp >= bp.xl) return e.xl;
    if (vp >= bp.lg) return e.lg;
    if (vp >= bp.md) return e.md;
    if (vp >= bp.sm) return e.sm;
    return hero.estimatedStackFallback;
  },

  initialTextNudgeDown(vp: number): number {
    const { bp, desktop } = BANNER3;
    const n = desktop.initialNudgeDownPx;
    if (vp >= bp.xl) return n.xl;
    if (vp >= bp.lg) return n.lg;
    if (vp >= bp.md) return n.md;
    if (vp >= bp.sm) return n.sm;
    return n.default;
  },

  heroTitle2EndMaxWidth(vp: number): number {
    const { bp, desktop } = BANNER3;
    const t = desktop.title2.endMaxPx;
    if (vp >= bp.xl) return t.xl;
    if (vp >= bp.lg) return t.lg;
    if (vp >= bp.md) return t.md;
    return desktop.title2.endWidthBelowMdPx;
  },

  heroTextBlockEndWidth(vp: number): number {
    const { bp, desktop } = BANNER3;
    const s = desktop.subtitleMaxEndPx;
    if (vp >= bp.xl) return s.xl;
    if (vp >= bp.md) return s.mdUp;
    return desktop.title2.endWidthBelowMdPx;
  },

  heroContentBlockEndWidth(vp: number): number {
    return Math.max(
      heroLayout.heroTextBlockEndWidth(vp),
      heroLayout.heroTitle2EndMaxWidth(vp),
    );
  },

  videoEndForViewport(vp: number): VideoEndLayout {
    const { bp, video } = BANNER3;
    if (vp >= bp.xl) return video.end.xl;
    if (vp >= bp.lg) return video.end.lgFrame;
    if (vp >= bp.md) return video.end.lg;
    if (vp >= bp.sm) return video.end.sm;
    return video.end.sm;
  },

  ctaEndTopExtraBelowCopy(vp: number): number {
    const { bp, desktop } = BANNER3;
    const x = desktop.ctaExtraBelowCopyPx;
    if (vp >= bp.lg) return x.lg;
    if (vp >= bp.md) return x.md;
    if (vp >= bp.sm) return x.sm;
    return 0;
  },

  accredStripWidth(vp: number): number {
    const { bp, accred } = BANNER3;
    const w = accred.stripWidthPx;
    if (vp >= bp.xl) return w.xl;
    if (vp >= bp.lg) return w.lg;
    if (vp >= bp.md) return w.md;
    return w.sm;
  },
} as const;

/**
 * True while a modal has taken over page scrolling.
 *
 * The wheel hijack below listens on `window`, so it keeps firing when a Radix
 * dialog (the lead popup) is open: scrolling INSIDE the popup still bubbled out
 * here and drove the parallax, dragging the background about behind the modal.
 * The dialog's own scroll lock does not help — it stops the body from
 * scrolling, it does not stop other listeners from receiving the event.
 *
 * `data-scroll-locked` is set on `<body>` by react-remove-scroll, which every
 * Radix modal in the app goes through, so one O(1) attribute check covers them
 * all. Deliberately not `body.style.pointerEvents`, which Dialog.tsx clears by
 * hand in places, nor a `querySelector` — this runs on every wheel event.
 */
const isPageScrollLocked = () =>
  typeof document !== "undefined" &&
  document.body.hasAttribute("data-scroll-locked");

function clipBannerText(value: unknown, maxLen: number): string {
  const s = String(value ?? "");
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen);
}

const Banner3 = (props: {
  data?: Record<string, unknown>;
  /** Admin page editor: scroll lives on a nested panel, not `window` — skip wheel/scroll hijack. */
  isCmsPreview?: boolean;
}) => {
  const { bp, text, mobile, desktop, hero, copy, wheel, accred } = BANNER3;
  const { data: sectionData, isCmsPreview = false } = props;
  const dispatch = useDispatch();
  const sectionRef = useRef<HTMLElement>(null);
  const stickyHeroRef = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  /** Viewport + hero box — paired updates avoid extra renders from separate width/height state. */
  const [viewport, setViewport] = useState<{ width: number; height: number }>(
    () => ({ width: BANNER3.bp.lg, height: 980 }),
  );
  const [heroMeasure, setHeroMeasure] = useState<{
    contentWidth: number;
    stickyHeight: number;
  }>(() => ({ contentWidth: 1430, stickyHeight: 0 }));
  const [scrollProgress, setScrollProgress] = useState(0);
  const wheelAccumRef = useRef(0);
  const wheelTargetRef = useRef(1);
  const parallaxCompleteRef = useRef(false);
  /** Page-offset of the section top — may be >0 when content exists above the banner. */
  const sectionScrollTopRef = useRef(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  /** `null` until first `matchMedia` read — avoids CTA flash (same role as old `ctaLayoutReady`). */
  const [isSmUp, setIsSmUp] = useState<boolean | null>(null);
  /** Mount `<video>` only after near-viewport (or immediately in CMS preview). */
  const [heroVideoLoadAllowed, setHeroVideoLoadAllowed] = useState(false);
  /** Hero video sound: default on; browsers that block unmuted autoplay fall back to muted until user taps (see `play()` effect). */
  const [heroVideoMuted, setHeroVideoMuted] = useState(false);
  const heroVideoMutedRef = useRef(false);
  heroVideoMutedRef.current = heroVideoMuted;
  const heroVideoRef = useRef<HTMLVideoElement | null>(null);

  const {
    status = true,
    hero_video_url = "",
    metadata: metadataFromData,
    slide_frequency: slideFrequencyFromData = 5000,
    accreditation_image_desktop = "",
    accreditation_image_mobile = "",
    title_1 = "Education That",
    title_1_color = "rgba(255,255,255,0.8)",
    title_2 = "Transforms",
    title_2_gradient_start_color = "",
    title_2_gradient_end_color = "",
    title_2_gradient_angle = "90deg",
    subtitle = "",
    subtitle_color = "#FFFFFF",
    download_brochure_text = "",
    apply_now_text = "Apply Now",
    explore_cta_url = "",
    apply_now_color = "#222222",
    button_color = "#FFFFFF",
    background_color = "#000000",
  } = (sectionData || {}) as Record<string, unknown>;

  const metadataRows = Array.isArray(metadataFromData) ? metadataFromData : [];
  const heroVideoSrc = String(hero_video_url ?? "").trim();
  const hasHeroVideo = heroVideoSrc.length > 0;

  const accredDesktopSrc = String(accreditation_image_desktop ?? "").trim();
  const accredMobileSrc = String(accreditation_image_mobile ?? "").trim();
  const accredBelowHeroSrc =
    accredMobileSrc.length > 0 ? accredMobileSrc : accredDesktopSrc;
  const hasHeroAccredDesktop = accredDesktopSrc.length > 0;
  const hasAccredBelowHero = accredBelowHeroSrc.length > 0;

  const slideFrequencyMs = useMemo(() => {
    const v = slideFrequencyFromData;
    if (typeof v === "number" && Number.isFinite(v)) return Math.max(500, v);
    const n = parseInt(String(v ?? "5000"), 10);
    return Math.max(500, Number.isFinite(n) ? n : 5000);
  }, [slideFrequencyFromData]);

  /** When `hero_video_url` is empty, these slides rotate in the same layer as the video. */
  const heroBackgroundSlides = useMemo(() => {
    return metadataRows
      .filter(
        (row): row is Record<string, unknown> =>
          row != null && typeof row === "object" && !Array.isArray(row),
      )
      .filter(
        (row) =>
          row.status !== false && String((row as { image?: unknown }).image ?? "").trim().length > 0,
      )
      .sort(
        (a, b) =>
          (Number((a as { priority?: unknown }).priority) || 0) -
          (Number((b as { priority?: unknown }).priority) || 0),
      )
      .map((row) => {
        const r = row as { image?: unknown; alt_text?: unknown; alt?: unknown };
        return {
          url: String(r.image).trim(),
          alt: String(r.alt_text ?? r.alt ?? "").trim() || "Hero background",
        };
      })
      .slice(0, copy.maxHeroBgSlides);
  }, [metadataRows]);

  /**
   * Override the CSS class gradient with a smooth linear gradient when the
   * CMS explicitly provides start/end colour values.
   */
  const hasCustomGradient =
    String(title_2_gradient_start_color).trim() !== "" &&
    String(title_2_gradient_end_color).trim() !== "";

  /** Visual 0→1; reduced-motion / CMS preview see the final (complete) layout. */
  const p = reduceMotion || isCmsPreview ? 1 : scrollProgress;
  /** Desktop hero layout + wheel parallax — from `--breakpoint-sm` (1080px) only. */
  const isDesktop = isSmUp === true;

  /** [LAYOUT][hydrate] S1 — First paint: sync viewport; read `prefers-reduced-motion` + desktop breakpoint (`sm`). */
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mqSmUp = window.matchMedia(`(min-width: ${bp.sm}px)`);
    setReduceMotion(mqReduce.matches);
    setIsSmUp(mqSmUp.matches);
  }, []);

  /** [FX][mq] S2 — Keep motion + breakpoint state in sync when user resizes or toggles OS motion settings. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mqSmUp = window.matchMedia(`(min-width: ${bp.sm}px)`);
    const onReduce = () => setReduceMotion(mqReduce.matches);
    const onSmUp = () => setIsSmUp(mqSmUp.matches);
    mqReduce.addEventListener("change", onReduce);
    mqSmUp.addEventListener("change", onSmUp);
    return () => {
      mqReduce.removeEventListener("change", onReduce);
      mqSmUp.removeEventListener("change", onSmUp);
    };
  }, []);

  /** [LAYOUT][measure] S3 — Track hero column width + sticky shell height for layout math (parallax, copy, accred). */
  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const sync = () => {
      setHeroMeasure({
        contentWidth: heroContentRef.current?.clientWidth ?? 1430,
        stickyHeight: stickyHeroRef.current?.clientHeight ?? 0,
      });
    };
    const ro = new ResizeObserver(sync);
    const content = heroContentRef.current;
    const sticky = stickyHeroRef.current;
    if (content) ro.observe(content);
    if (sticky) ro.observe(sticky);
    sync();
    return () => ro.disconnect();
  }, []);

  /**
   * [FX][media-gate] S4 — Allow mounting heavy hero media: preview → always; images-only → immediate;
   * video → `IntersectionObserver` (near viewport) then allow.
   */
  useEffect(() => {
    if (!status) {
      setHeroVideoLoadAllowed(false);
      return;
    }
    if (isCmsPreview) {
      setHeroVideoLoadAllowed(true);
      return;
    }
    const slideCount = heroBackgroundSlides.length;
    if (!hasHeroVideo && slideCount === 0) {
      setHeroVideoLoadAllowed(false);
      return;
    }
    if (!hasHeroVideo && slideCount > 0) {
      setHeroVideoLoadAllowed(true);
      return;
    }
    const root = sectionRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (hit) {
          setHeroVideoLoadAllowed(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: "180px 0px", threshold: 0 },
    );
    io.observe(root);
    return () => io.disconnect();
  }, [hasHeroVideo, heroBackgroundSlides.length, hero_video_url, isCmsPreview, status]);

  const [heroBgImageIndex, setHeroBgImageIndex] = useState(0);

  const heroBgSlideUrlsKey = useMemo(
    () => heroBackgroundSlides.map((s) => s.url).join("|"),
    [heroBackgroundSlides],
  );

  /** [FX][carousel-reset] S5 — Slide URL set changed → show first slide. */
  useEffect(() => {
    setHeroBgImageIndex(0);
  }, [heroBgSlideUrlsKey]);

  /** [FX][carousel-tick] S6 — Advance carousel index on an interval (crossfade-safe spacing). */
  useEffect(() => {
    if (hasHeroVideo || heroBackgroundSlides.length <= 1) return;
    if (reduceMotion) return;
    const advanceEvery = Math.max(
      slideFrequencyMs,
      hero.bgCrossfadeMs + 150,
    );
    const id = window.setInterval(() => {
      setHeroBgImageIndex(
        (i) => (i + 1) % heroBackgroundSlides.length,
      );
    }, advanceEvery);
    return () => clearInterval(id);
  }, [hasHeroVideo, heroBackgroundSlides.length, slideFrequencyMs, reduceMotion]);

  const showHeroVideoLayer = hasHeroVideo && heroVideoLoadAllowed;
  const showHeroImageCarousel =
    !hasHeroVideo && heroBackgroundSlides.length > 0 && heroVideoLoadAllowed;

  /** [FX][video-reset-mute] S7 — New `hero_video_url` → reset mute preference to default (sound on). */
  useEffect(() => {
    heroVideoMutedRef.current = false;
    setHeroVideoMuted(false);
  }, [heroVideoSrc]);

  /**
   * [FX][video-play] S8 — When video is allowed + mounted: autoplay; if unmuted play fails, fall back to muted
   * (autoplay policy).
   */
  useEffect(() => {
    const v = heroVideoRef.current;
    if (!v || !hasHeroVideo || !heroVideoLoadAllowed) return;
    let cancelled = false;
    const attempt = () => {
      if (cancelled) return;
      v.muted = heroVideoMutedRef.current;
      void v.play().catch(() => {
        if (cancelled) return;
        if (!heroVideoMutedRef.current) {
          setHeroVideoMuted(true);
          v.muted = true;
          heroVideoMutedRef.current = true;
        }
        void v.play();
      });
    };
    v.addEventListener("canplay", attempt, { once: true });
    attempt();
    return () => {
      cancelled = true;
      v.removeEventListener("canplay", attempt);
    };
  }, [hasHeroVideo, heroVideoLoadAllowed, heroVideoSrc]);

  /** Tap / click empty hero toggles sound; CTA and other controls stay interactive. */
  const onHeroVideoSoundToggleCapture = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement | null;
      if (
        !t ||
        t.closest(
          "a, button, [role='button'], input, textarea, select, [data-hero-sound-toggle-ignore]",
        )
      ) {
        return;
      }
      if (!hasHeroVideo || !heroVideoLoadAllowed) return;
      const v = heroVideoRef.current;
      if (!v) return;
      const next = !v.muted;
      setHeroVideoMuted(next);
      heroVideoMutedRef.current = next;
      v.muted = next;
      void v.play();
    },
    [hasHeroVideo, heroVideoLoadAllowed],
  );

  /**
   * [FX][viewport] S9 — `resize`: update viewport, wheel parallax distance target (`wheelTargetRef`),
   * and section top offset (`sectionScrollTopRef`) for wheel / scroll-lock logic.
   */
  useEffect(() => {
    const syncViewport = () => {
      if (typeof window === "undefined") return;
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      wheelTargetRef.current =
        window.innerHeight * wheel.deltaViewportMultiplier;
      if (sectionRef.current) {
        sectionScrollTopRef.current = sectionRef.current.offsetTop;
      }
    };
    syncViewport();
    let debounceTimer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(syncViewport, 100);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(debounceTimer);
    };
  }, []);

  /**
   * [FX][parallax-wheel] S10 — Desktop only: intercept wheel at hero top to drive `scrollProgress` 0→1,
   * then raw progress toward unlock; wheel-up at document top reverses. Skipped for reduced-motion / preview / non-desktop.
   */
  useEffect(() => {
    if (reduceMotion || isCmsPreview) {
      setScrollProgress(1);
      parallaxCompleteRef.current = true;
      return;
    }
    if (isSmUp !== true) {
      setScrollProgress(0);
      parallaxCompleteRef.current = false;
      wheelAccumRef.current = 0;
      return;
    }

    /** True when the section is at (or has just reached) the top of the viewport. */
    const atHeroTop = () =>
      window.scrollY <= sectionScrollTopRef.current + 8;

    const onWheel = (e: WheelEvent) => {
      // A modal owns scrolling while it is open — leave the parallax alone.
      if (isPageScrollLocked()) return;
      const parallaxTarget = wheelTargetRef.current;
      if (parallaxTarget <= 1) return;

      const maxAccum = parallaxTarget * wheel.scrollUnlockProgress;

      const applyAccumDelta = () => {
        wheelAccumRef.current += e.deltaY;
        wheelAccumRef.current = Math.max(0, Math.min(maxAccum, wheelAccumRef.current));
        const raw = wheelAccumRef.current / parallaxTarget;
        setScrollProgress(Math.min(1, raw));
        if (raw >= wheel.scrollUnlockProgress - 1e-6) {
          parallaxCompleteRef.current = true;
        } else {
          parallaxCompleteRef.current = false;
        }
      };

      // Unlocked: page may scroll down; wheel-up at document top reverses buffer then parallax.
      if (parallaxCompleteRef.current) {
        if (!atHeroTop()) return;
        if (e.deltaY >= 0) return;

        e.preventDefault();
        e.stopPropagation();
        applyAccumDelta();
        return;
      }

      if (!atHeroTop()) return;

      e.preventDefault();
      e.stopPropagation();
      applyAccumDelta();
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [isSmUp, reduceMotion, isCmsPreview]);

  /**
   * [FX][parallax-scroll-lock] S11 — While parallax not “unlocked”, snap document scroll back to banner top
   * (covers keyboard / scrollbar / programmatic scroll during the lock window).
   */
  useEffect(() => {
    if (isSmUp !== true || reduceMotion || isCmsPreview) return;
    const onScroll = () => {
      // Never yank the document back while a modal is open.
      if (isPageScrollLocked()) return;
      if (parallaxCompleteRef.current) return;
      const sectionTop = sectionScrollTopRef.current;
      // Only lock if the section is actually in / at the top of the viewport.
      if (window.scrollY > sectionTop) {
        window.scrollTo({ top: sectionTop, behavior: "instant" });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isSmUp, reduceMotion, isCmsPreview]);

  const effectiveP = isDesktop ? p : 0;

  /** Desktop headline/video/accred geometry — single memo shares `viewport.width` + `heroMeasure` reads. */
  const layoutMetrics = useMemo(() => {
    const vw = viewport.width;
    const vh = viewport.height;
    const contentW = heroMeasure.contentWidth;
    const stickyH = heroMeasure.stickyHeight;

    const resolvedHeroHeightPx =
      stickyH > 0 ? stickyH : heroLayout.desktopStickyHeight(vw) ?? vh;

    const estimated = heroLayout.estimatedTextBlockHeight(vw);
    const nudge = heroLayout.initialTextNudgeDown(vw);
    const bottomPad = text.bottomPadPx + text.safeBottomPx;
    const maxTopForStack = resolvedHeroHeightPx - estimated - bottomPad;
    const rawTop = maxTopForStack + nudge;
    const textTopStartDesktopPx = Math.max(
      text.safeTopPx,
      Math.min(rawTop, maxTopForStack),
    );

    const { cta } = desktop;
    const ctaTopStartDesktopPx = Math.max(
      text.safeTopPx,
      textTopStartDesktopPx - cta.gapAboveTextPx - cta.estHeightPx,
    );

    const figmaW = heroLayout.accredStripWidth(vw);
    const cap =
      contentW > 0 ? Math.max(0, contentW - 32) : Math.max(0, vw - 32);

    return {
      resolvedHeroHeightPx,
      textTopStartDesktopPx,
      ctaTopStartDesktopPx,
      accredDesktopWidthPx: Math.min(figmaW, cap),
      videoEndLayout: heroLayout.videoEndForViewport(vw),
    };
  }, [viewport, heroMeasure, text, desktop]);

  const {
    resolvedHeroHeightPx,
    textTopStartDesktopPx,
    ctaTopStartDesktopPx,
    accredDesktopWidthPx,
    videoEndLayout,
  } = layoutMetrics;

  /** sm+: fades in with parallax (0→1); below sm or until breakpoint hydrates: full opacity. */
  const heroLogosOpacity =
    !isSmUp || reduceMotion ? 1 : lerp(0, 1, effectiveP);

  /** [FX][analytics] S12 — One-time view event when the section is active (skipped in CMS preview). */
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (!isCmsPreview && sectionRef.current && status) {
  //     FireWidgetViewedEvent(sectionRef, "banner_3");
  //   }
  // }, [status, isCmsPreview]);

  const ctaLabel =
    String(apply_now_text || "").trim() ||
    String(download_brochure_text || "").trim() ||
    "Apply Now";

  const displayTitle1 = clipBannerText(title_1, copy.maxTitle1Len);
  const displayTitle2 = clipBannerText(title_2, copy.maxTitle2Len);
  const displaySubtitle = clipBannerText(subtitle, copy.maxSubtitleLen);

  const heroTitle2LerpStartPx =
    viewport.width >= bp.md
      ? desktop.title2.lerpStartMdUpPx
      : desktop.title2.initialMaxSmPx;

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "banner_3",
      component: "hero_cta",
      label: ctaLabel,
      step: "1",
      entity: "banner_3",
      parentEntity: "banner_3",
    });
    const raw = String(explore_cta_url ?? "").trim();
    if (!raw || raw === "#") {
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
      return;
    }
    if (raw.startsWith("#")) {
      const id = raw.slice(1);
      if (id) document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      else dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
      return;
    }
    window.location.assign(raw);
  }, [ctaLabel, dispatch, explore_cta_url]);

  const videoEndHeightPx = videoEndLayout.endHeightPx;
  const videoEndBottomPx = Math.max(
    0,
    resolvedHeroHeightPx - text.topFigmaEndPx - videoEndHeightPx,
  );
  const videoHeightPx = lerp(resolvedHeroHeightPx, videoEndHeightPx, effectiveP);
  const videoBottomPx = lerp(0, videoEndBottomPx, effectiveP);

  /** Parallax end: headline left edge meets CTA left (54px) at every desktop width. */
  const textLeftPx = lerp(
    heroMeasure.contentWidth / 2 - text.leftOffsetStartPx,
    text.ctaFinalLeftPx,
    effectiveP,
  );

  const videoEndWidthPx = videoEndLayout.endWidthPx;
  const videoWrapStyle: CSSProperties = isDesktop
    ? {
        width:
          videoEndWidthPx != null
            ? `${lerp(heroMeasure.contentWidth, videoEndWidthPx, effectiveP)}px`
            : `${lerp(100, videoEndLayout.widthPct, effectiveP)}%`,
        height: `${videoHeightPx}px`,
        left: `${lerp(50, videoEndLayout.leftPct, effectiveP)}%`,
        bottom: `${videoBottomPx}px`,
        transform: "translateX(-50%)",
      }
    : {
        inset: 0,
        width: "100%",
        height: "100%",
        transform: "none",
      };

  const textBlockStyle: CSSProperties | undefined = isDesktop
    ? {
        width: `${lerp(
          720,
          heroLayout.heroContentBlockEndWidth(viewport.width),
          effectiveP,
        )}px`,
        maxWidth: "min(720px, calc(100% - 32px))",
        left: `${textLeftPx}px`,
        top: `${lerp(
          textTopStartDesktopPx,
          text.topFigmaEndPx,
          effectiveP,
        )}px`,
        bottom: "auto",
        transform: "none",
        textAlign: effectiveP > 0.45 ? "left" : "center",
      }
    : {
        left: "50%",
        width: "100%",
        maxWidth: `min(${mobile.frameWidthPx}px, calc(100% - ${mobile.textPadXPx * 2}px))`,
        paddingLeft: `${mobile.textPadXPx}px`,
        paddingRight: `${mobile.textPadXPx}px`,
        top: `${mobile.textTopPx}px`,
        bottom: "auto",
        transform: "translateX(-50%)",
        textAlign: "center",
        boxSizing: "border-box",
      };

  const ctaEndTopPx =
    viewport.width >= bp.lg
      ? desktop.cta.topPx + desktop.cta.endTopNudgeFromLgPx
      : desktop.cta.topPx;

  const ctaStyle: CSSProperties = isDesktop
    ? {
        left: `${lerp(
          desktop.cta.parallaxStartLeftPx,
          text.ctaFinalLeftPx,
          effectiveP,
        )}px`,
        top: `${lerp(
          ctaTopStartDesktopPx,
          ctaEndTopPx +
            desktop.cta.endMarginTopPx +
            heroLayout.ctaEndTopExtraBelowCopy(viewport.width),
          effectiveP,
        )}px`,
        bottom: "auto",
      }
    : {
        left: "50%",
        top: `${mobile.ctaTopPx}px`,
        bottom: "auto",
        transform: "translateX(-50%)",
        width: `${mobile.ctaWidthPx}px`,
      };

  /** Near parallax complete — switch gradient headline to block layout (no `line-clamp-1` ellipsis). */
  const isHeroTitleEndClamp = isDesktop && effectiveP > 0.985;

  /**
   * sm+ (≥1080): strip in hero only; bottom 60→end px and `heroLogosOpacity` with parallax.
   * Under 1080px: strip is below hero (mobile block), not this absolute layer.
   */
  const heroLogosStyle: CSSProperties = {
    left: 0,
    right: 0,
    top: "auto",
    bottom: `${lerp(
      accred.parallaxBottomStartPx,
      heroLayout.accredBottomEnd(viewport.width),
      effectiveP,
    )}px`,
    width: "100%",
    transform: "none",
  };

  const videoMime = /\.webm(\?|$)/i.test(heroVideoSrc)
    ? "video/webm"
    : /\.mov(\?|$)/i.test(heroVideoSrc)
      ? "video/quicktime"
      : "video/mp4";

  if (!status) return null;

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-x-hidden"
      style={{ backgroundColor: background_color as string }}
    >
      {/* Desktop: static hero height per Figma — 4731:10474 771px, …; mobile &lt;1080: 707px. */}
      <div className="relative w-full">
        <div
          ref={stickyHeroRef}
          className={cn(
            "sticky top-0 z-10 overflow-hidden",
            "max-[1079px]:min-h-[707px]",
            "sm:h-[771px] sm:min-h-[771px]",
            "md:h-[805px] md:min-h-[805px]",
            "lg:h-[980px] lg:min-h-[980px]",
            "xl:h-[1080px] xl:min-h-[1080px]",
          )}
        >
          <div
            ref={heroContentRef}
            onClickCapture={onHeroVideoSoundToggleCapture}
            className={cn(
              "relative mx-auto h-full w-full",
              /* Under 1080px: mobile column height so absolute video (inset 0) fills */
              "max-[1079px]:min-h-[707px]",
              /* <1080: full width; 1080–1429: 1430 cap; lg–xl: 1600; xl: 1750 — less horizontal gutter */
              "max-[1079px]:max-w-none",
              "min-[1080px]:max-w-[1430px] lg:max-w-[1630px] xl:max-w-[1750px]",
            )}
          >
            {/* Video (parallax) or image carousel — infinite crossfade; no manual swipe (layer is pointer-events-none). */}
            <div
              className="pointer-events-none absolute z-0 overflow-hidden"
              style={videoWrapStyle}
            >
              {showHeroVideoLayer ? (
                <video
                  ref={heroVideoRef}
                  className="absolute size-full max-w-none object-cover"
                  autoPlay
                  muted={heroVideoMuted}
                  playsInline
                  loop
                  controlsList="nodownload"
                  preload="none"
                >
                  <source src={heroVideoSrc} type={videoMime} />
                </video>
              ) : showHeroImageCarousel ? (
                heroBackgroundSlides.map((slide, i) => {
                  const isActive = i === heroBgImageIndex;
                  return (
                    <img
                      key={slide.url}
                      src={slide.url}
                      alt={isActive ? slide.alt : ""}
                      {...(!isActive ? { "aria-hidden": true as const } : {})}
                      className={cn(
                        "absolute inset-0 size-full max-w-none object-cover select-none [-webkit-user-drag:none]",
                        reduceMotion
                          ? isActive
                            ? "z-[2] opacity-100"
                            : "z-[1] opacity-0"
                          : cn(
                              "ease-in-out transition-opacity",
                              isActive
                                ? "z-[2] opacity-100"
                                : "z-[1] opacity-0",
                            ),
                      )}
                      style={
                        reduceMotion
                          ? undefined
                          : {
                              transitionDuration: `${hero.bgCrossfadeMs}ms`,
                            }
                      }
                      draggable={false}
                      tabIndex={-1}
                      decoding="async"
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  );
                })
              ) : (
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundColor: (background_color as string) || "#000000",
                  }}
                  aria-hidden
                />
              )}
            </div>

            <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)" }} aria-hidden />

            {/* Headline + copy — Figma 3018:10017 / 3018:10548 */}
            <div
              className={cn(
                "absolute z-[2] flex flex-col",
                /* Figma 3583:10527 gap 12px title ↔ body; desktop gap 24 */
                isDesktop ? "gap-6" : "gap-2",
                isDesktop &&
                  "w-[min(720px,calc(100%-32px))] max-w-full tab:max-w-[468px] sm:max-w-[508px]",
              )}
              style={textBlockStyle}
            >
              <div
                className={cn(
                  "flex flex-col",
                  !isDesktop && "w-full min-w-0 max-w-full gap-0 items-center",
                  isDesktop && "gap-0",
                  isDesktop && (effectiveP > 0.45 ? "items-start" : "items-center"),
                )}
                style={isDesktop ? { width: "100%" } : undefined}
              >
                <p
                  className={cn(
                    "m-0 w-full min-w-0 font-medium leading-normal tracking-normal",
                    !isDesktop && "line-clamp-1 text-center",
                    isDesktop && "break-words",
                    "text-[26px]",
                    "sm:text-[32px]",
                    "md:text-[40px]",
                    "lg:text-[40px]",
                    "xl:text-[40px]"
                  )}
                  style={{
                    color: title_1_color as string,
                    fontFamily: "var(--font-google-sans)",
                    ...(isDesktop
                      ? {
                          maxWidth: `${lerp(
                            720,
                            heroLayout.heroTextBlockEndWidth(viewport.width),
                            effectiveP,
                          )}px`,
                        }
                      : {
                          maxWidth: `min(${mobile.headlineInnerMaxWPx}px, 100%)`,
                        }),
                  }}
                >
                  {displayTitle1}
                </p>
                <span
                  className={cn(
                    "m-0 font-bold leading-[1.2] gradient-title-denton",
                    /* Mobile — Figma 3583:10530: 54px Merriweather, centered; wrap to multiple lines so long `title_2` stays visible. */
                    !isDesktop
                      ? "block w-full min-w-0 max-w-full text-center whitespace-normal break-words text-[48px]! tracking-[0]!"
                      : isHeroTitleEndClamp
                        ? "block w-full min-w-0 break-words whitespace-normal"
                        : "inline-block",
                    /* Desktop (`min-width: 1080px` == `isDesktop`): sizes use project breakpoints, not default Tailwind `sm` (640px). */
                    isDesktop &&
                      cn(
                        "min-[1080px]:text-[64px]!",
                        "min-[1230px]:text-[78px]!",
                        "min-[1430px]:text-[92px]!",
                      ),
                  )}
                  style={{
                    fontFamily: "var(--font-merriweather)",
                    ...(isDesktop && {
                      maxWidth: `${lerp(
                        heroTitle2LerpStartPx,
                        heroLayout.heroTitle2EndMaxWidth(viewport.width),
                        effectiveP,
                      )}px`,
                    }),
                    ...(hasCustomGradient && {
                      backgroundImage: `linear-gradient(${title_2_gradient_angle}, ${title_2_gradient_start_color}, ${title_2_gradient_end_color})`,
                    }),
                  }}
                >
                  {displayTitle2}
                </span>
              </div>
              <p
                className={cn(
                  "m-0 max-w-full min-w-0 font-medium leading-[1.4]",
                  isDesktop
                    ? "line-clamp-6"
                    : "line-clamp-3",
                  isDesktop
                    ? /* Figma 4731:10483 · 4731:10837 · 4303:13662 — 20px / 0.2px tracking from 1080px */
                      "text-[16px] tracking-[0.12px] tab:text-[18px] tab:tracking-[0.18px] sm:text-[20px] sm:tracking-[0.2px]"
                    : "text-[12px] tracking-[0.12px]",
                  !isDesktop && "px-0",
                )}
                style={{
                  color: subtitle_color as string,
                  fontFamily: "var(--font-google-sans)",
                  maxWidth: isDesktop
                    ? `${lerp(
                        desktop.subtitleLerpStartPx,
                        viewport.width < bp.md
                          ? desktop.subtitleMaxEndPx.sm
                          : viewport.width >= bp.xl
                            ? desktop.subtitleMaxEndPx.xl
                            : desktop.subtitleMaxEndPx.mdUp,
                        effectiveP,
                      )}px`
                    : "100%",
                }}
              >
                {displaySubtitle}
              </p>
            </div>

            {/* CTA — desktop: Figma 4731:10476 / 10344 / 4303:13655 / 4731:10957 (px-24 py-16 gap-10, 16px / 0.16px); .btn-base matches; mobile: 3583:10532 */}
            <div
              className={cn(
                "absolute z-[3]",
                isSmUp === null &&
                  "pointer-events-none opacity-0 transition-none [&_button]:transition-none",
              )}
              style={ctaStyle}
            >
              <button
                type="button"
                onClick={handleCtaClick}
                aria-label={ctaLabel}
                data-cta-name="apply_now"
                className={cn(
                  "btn-base",
                  "shadow-none",
                  "border border-solid",
                  isDesktop
                    ? "h-auto min-h-0 whitespace-nowrap !px-10 py-4 gap-2.5 w-max max-w-[min(100%,calc(100%-32px))]"
                    : "w-full justify-center !px-7 !py-3 !gap-2.5 !text-[14px] !leading-[1.4] !tracking-[0.14px]",
                )}
                style={{
                  fontFamily: "var(--font-google-sans)",
                  color: String(apply_now_color),
                  backgroundColor: String(button_color),
                  borderColor: String(apply_now_color),
                  ...(!isDesktop
                    ? {
                        minHeight: mobile.ctaHeightPx,
                        height: mobile.ctaHeightPx,
                      }
                    : undefined),
                }}
              >
                {ctaLabel}
                <ArrowRight
                  className={cn(
                    "shrink-0",
                    isDesktop ? "size-6" : "size-5",
                  )}
                  aria-hidden
                />
              </button>
            </div>

            {/* Accreditation strip — desktop/tab in hero (parallax fade on sm+); Figma 4731:10534 / 4303:13430 */}
            {hasHeroAccredDesktop && isDesktop ? (
              <div
                className="absolute z-[4] flex items-center justify-center"
                style={{ ...heroLogosStyle, opacity: heroLogosOpacity }}
              >
                <Image
                  src={accredDesktopSrc}
                  alt="Accreditation and rankings"
                  width={1470}
                  height={98}
                  className="h-auto max-w-full object-contain object-center"
                  style={{
                    width: accredDesktopWidthPx,
                    height: "auto",
                  }}
                  unoptimized
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Mobile accreditation — below hero (3583:10535) */}
      {hasAccredBelowHero && !isDesktop ? (
        <div
          className="px-3"
          style={{ backgroundColor: background_color as string }}
        >
          <div className="mx-auto w-full max-w-[450px]">
            <Image
              src={accredBelowHeroSrc}
              alt="Accreditation and rankings"
              width={450}
              height={200}
              className="h-auto w-full object-contain object-center"
              style={{ height: "auto" }}
              unoptimized
            />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default memo(Banner3);
